<template>
  <main
    ref="chatMainWrap"
    :class="[
      style['chat-wrap'],
      { [style['no-login']]: hideSignIn },
      { [style['no-header']]: isApp }
    ]"
  >
    <!-- Spinner -->
    <div v-if="chatStore.loading" :class="style['chat-body-loader']">
      <spinner-chat-dot :class-name="'spinner-wrapper'" />
    </div>
    <!-- /Spinner -->
    <!-- Chat message loop -->
    <template v-else>
      <template v-for="(chat, index) in transformedChatMessages">
        <div v-if="printDate(chat.published_at, index)" :key="`${chat.id}-separator`" class="date">
          <h3 class="text-divider">
            <time :datetime="chat.published_at">
              {{ chat.published_at | formatDate }}
            </time>
          </h3>
        </div>
        <component
          :is="chatWidget(chat)"
          :id="`msg-${chat.id}`"
          :key="chat.id"
          v-bind="widgetProps(chat, index)"
          @delete="handleDeleteMessage"
        />
      </template>
    </template>
    <!-- /Chat message loop -->
    <div v-if="isTyping || isServerResponding || isMessageDelaying" class="msg tripla">
      <p>
        <spinner-chat-dot />
      </p>
    </div>
    <div class="spacer"></div>
    <t-alert
      v-if="isBrowserObsolete()"
      :class="[style['warning']]"
      :alerts="$t('browser_warning')"
      dismissible
      variant="warning"
    >
    </t-alert>

    <t-modal :visible.sync="showConfirmMessageModal">
      <div class="p-4">
        <div class="text-center">
          <h3 class="mb-2">{{ $t('chat_body.delete_message') }}</h3>
          {{ $t('chat_body.delete_message_desc') }}
        </div>

        <div class="flex items-center justify-center mt-4">
          <theme-button class="w-1/2" outline @click="showConfirmMessageModal = false">{{
            $t('base.cancel')
          }}</theme-button>
          <theme-button class="w-1/2 ml-2" @click="deleteChatMessage(processingChatMessage)">{{
            $t('base.delete')
          }}</theme-button>
        </div>
      </div>
    </t-modal>

    <slot></slot>
  </main>
</template>

<script>
import style from './layout.scss'
import { Chat } from 'src/apis'
import { EventBus } from 'ChatWidget/EventBus'
import ChatMessage from './Chats/ChatMessage.vue'
import ActionWidget from './Chats/ActionWidget.vue'
import AdvertismentWidget from './Chats/AdvertismentWidget.vue'
import AutoMessageWidget from './Chats/AutoMessageWidget.vue'
import BrrWidget from './Chats/BrrWidget.vue'
import CreditCardWidget from './Chats/CreditCardWidget.vue'
import DateWidget from './Chats/DateWidget.vue'
import DateTimeWidget from './Chats/DateTimeWidget.vue'
import EmailWidget from './Chats/EmailWidget.vue'
import TemplateWidget from './Chats/TemplateWidget.vue'
import SelectorWidget from './Chats/SelectorWidget.vue'
import LoginWidget from './Chats/LoginWidget.vue'
import LanguageWidget from './Chats/LanguageWidget.vue'
import MediaWidget from './Chats/MediaWidget.vue'
import MultiSelectWidget from './Chats/MultiSelectWidget.vue'
import SliderWidget from './Chats/SliderWidget.vue'
import QuickReplyWidget from './Chats/QuickReplyWidget.vue'
import UserEvaluationWidget from './Chats/UserEvaluationWidget.vue'
import ImageListWidget from './Chats/ImageListWidget.vue'
import ImageWidget from './Chats/ImageWidget.vue'
import UploadError from './Chats/UploadError.vue'
import SpinnerChatDot from 'UtilityComponents/SpinnerChatDot.vue'
import TAlert from '../../Common/TAlert'
import { isWebView } from 'Utilities/appAccess'
import FigureCaption from 'Chats/FigureCaption'
import ThemeButton from 'javascript/Common/ThemeButton'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import { useUserStore } from 'Store/user'
import { useLazyLoadStore } from 'Store/lazy-load'
import { useTermsConditionsStore } from 'Store/terms-conditions'
import debounce from 'lodash.debounce'
import { useAppStore } from 'Store/app'
import { useBottomPanelStore } from 'Store/bottom-panel'
import { useBrandChatbotStore } from 'Store/brand-chatbot'
import { useSettingsStore } from 'Store/settings'
import { mapState } from 'pinia'
import { useChatStore } from 'Store/chat'
import { useColorsStore } from 'Store/colors'
import { useDetailsPageStore } from 'Store/details-page'
import { useFormStore } from 'Store/form'
import { useIconMessageStore } from 'Store/icon-message'
import Url from 'url-parse'
import { useUserPanelStore } from 'Store/user-panel'

const availableLanguages = [
  { locale: 'en', text: 'English', selected: false },
  { locale: 'ja', text: '日本語', selected: false },
  { locale: 'zh_Hant', text: '中文繁體', selected: false },
  { locale: 'zh_Hans', text: '中文簡体', selected: false },
  { locale: 'ko', text: '한국어', selected: false },
  { locale: 'id', text: 'Indonesian', selected: false },
  { locale: 'ar', text: 'العربية', selected: false },
  { locale: 'th', text: 'ไทย', selected: false }
]

const languageSelector = {
  concierge: {
    image_url: '/assets/concierges/photos/images/1/medium/fu.png?1467302107',
    name: 'Furukawa',
    nickname: 'Fu'
  },
  published_at: Date.now(),
  message: 'Please select your language',
  message_rich: {
    type: 'language',
    active: true,
    message: 'Please select your language',
    actions: availableLanguages
  }
}

export default {
  components: {
    ThemeButton,
    ChatMessage,
    FigureCaption,
    ActionWidget,
    AdvertismentWidget,
    AutoMessageWidget,
    BrrWidget,
    CreditCardWidget,
    DateWidget,
    DateTimeWidget,
    EmailWidget,
    TemplateWidget,
    SelectorWidget,
    LoginWidget,
    LanguageWidget,
    MediaWidget,
    MultiSelectWidget,
    SliderWidget,
    QuickReplyWidget,
    UserEvaluationWidget,
    ImageListWidget,
    ImageWidget,
    UploadError,
    SpinnerChatDot,
    TAlert
  },

  filters: {
    formatDate(date) {
      const d = new Date(date)
      const day = d.getDate()
      const month = d.getMonth()
      const year = d.getFullYear()

      return `${year} / ${month + 1} / ${day}`
    }
  },

  props: {
    choosePaymentMethod: { type: Function, required: true },
    hideSignIn: { type: Boolean, required: true },
    imageURL: { type: String, required: true },
    newPaymentMethod: { type: Function, required: true },
    operatorName: { type: String, required: true },
    toggleMenu: { type: Function, required: true },
    openUserPanel: { type: Function, required: true },
    widgetInitial: { type: Boolean, required: true }
  },

  setup() {
    return {
      userStore: useUserStore(),
      termsConditionsStore: useTermsConditionsStore(),
      lazyLoadStore: useLazyLoadStore(),
      appStore: useAppStore(),
      bottomPanelStore: useBottomPanelStore(),
      brandChatbotStore: useBrandChatbotStore(),
      settingsStore: useSettingsStore(),
      chatStore: useChatStore(),
      colorsStore: useColorsStore(),
      detailsPageStore: useDetailsPageStore(),
      formStore: useFormStore(),
      iconMessageStore: useIconMessageStore(),
      userPanelStore: useUserPanelStore()
    }
  },
  data() {
    return {
      showConfirmMessageModal: false,
      processingChatMessage: null,
      switchBotTypingFeature: true,
      chatOpenEmailTrigger: false,
      style
    }
  },
  computed: {
    ...mapState(useChatStore, {
      chatMessages: 'messages',
      pubnubObject: 'pubnub'
    }),

    ...mapState(useChatStore, ['chat', 'isTyping', 'isServerResponding', 'isMessageDelaying']),

    ...mapState(useSettingsStore, {
      settings: 'getSettings',
      isApp: 'isApp',
      isFacilitySignInEnabled: 'getFacilitySignIn'
    }),

    isKnownUser() {
      return this.userStore.isKnownUser
    },

    transformedChatMessages() {
      return this.chatMessages.map((chat) => {
        if (
          chat.message_rich &&
          chat.message_rich.auto_message_info &&
          chat.message_rich.auto_message_info.type === 'auth'
        ) {
          chat.message_rich.active = !this.isKnownUser
          chat.message_rich.actions = [{ text: this.$t('new_session.submit') }]
        }

        return chat
      })
    }
  },
  watch: {
    widgetInitial: {
      handler(initialValue) {
        // TODO: find a way to get rid of EventBus here, maybe vuex can help
        // we only want to fetch chat and messages when users open chat window for the first time
        // so we're using this widgetInitial to determine whether chat window is opened first time
        // when chat window is open for the first time, there's an event update-chat-iframe emitted from EventBus to set this value to false
        if (!initialValue && isWebView) {
          this.loadAppChat()
        } else if (!initialValue) {
          this.openLoginDefault()
          this.chatStore.fetchChats()
        }
      },
      immediate: true
    }
  },
  created() {
    // refresh login called from Users/SignIn.vue
    EventBus.$on('refresh-login', (json) => {
      if (this.chat.id !== json.data.chat.id) {
        this.chatStore.unsubscribeChat(this.chat)
        this.chatStore.removeChat()
        this.chatStore.setChat(json.data.chat)

        triplaLocalStorage.setItem('triplabot_known_user_id', json.data.user.known_user.id)
        triplaLocalStorage.setItem('triplabot_auth', 'triplabot_sign_in')
        if (json.data.user.known_user_signed_in) {
          triplaLocalStorage.setItem('triplabot_known_user_signed_in', true)
        }

        this.userStore.setUser(json.data.user)
        this.iconMessageStore.fetchCDPIconMessages()
        this.chatStore.fetchChats()
      }
    })
  },

  mounted() {
    EventBus.$on('scroll-to-latest-message', this.scrollToLatestMessage)

    // scroll when the slider widget comes
    EventBus.$on('scroll-to-slider-message', (elementHeight) => {
      setTimeout(() => {
        this.$refs.chatMainWrap.scrollTop =
          this.$refs.chatMainWrap.scrollHeight - elementHeight - 50 // 40px height space with 10px offset
      }, 10)
    })

    // show language menu is emitted from ./ChatMenu.vue
    EventBus.$on('show-language-menu', (isToggleMenu = true) => {
      if (isToggleMenu) {
        this.toggleMenu()
      }
      const availableLocales = this.settings.bot_languages
      languageSelector.message = this.$t('select_language')
      languageSelector.message_rich.message = this.$t('select_language')
      languageSelector.message_rich.actions = availableLocales
        .map((l) => availableLanguages.find((language) => language.locale === l))
        .filter((l) => l)
      languageSelector.published_at = Date.now()
      this.chatStore.pushChatMessage(languageSelector)
    })

    EventBus.$on('app-change-chat', (code, options = {}) => {
      this.changeChat(code, options)
    })
  },
  updated() {
    // Always scroll to bottom when chat is updated
    this.$refs.chatMainWrap.scrollTop = this.$refs.chatMainWrap.scrollHeight
  },
  methods: {
    openLoginDefault() {
      const url = new Url(window.location.toString(), '/', true)
      if (url.query.triplabot_login === 'true' && !this.isKnownUser)
        this.userPanelStore.openPanel('SignIn')
    },

    scrollToLatestMessage: debounce(function () {
      const lastMessage = [...this.chatMessages]
        .reverse()
        .find((m) => m.message_rich.type !== 'quick_reply')

      const el = this.$el.querySelector(`#msg-${lastMessage.id}`)
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      })
    }, 100),

    handleDeleteMessage(chatMessage) {
      this.processingChatMessage = chatMessage
      this.showConfirmMessageModal = true
    },

    deleteChatMessage(chatMessage) {
      const chatMsg = this.chatMessages.find((cm) => cm.id === chatMessage.id)
      this.$set(chatMsg, 'deleted_at', Date.now())
      this.showConfirmMessageModal = false
      this.processingChatMessage = null
      Chat.deleteMessage(chatMessage.id)
    },

    checkChat(chat) {
      if (chat.hasOwnProperty('concierge')) {
        return 'tripla'
      }
      if (chat.hasOwnProperty('user')) {
        return 'cust'
      }
    },

    loadAppChat() {
      Chat.initAppChat().then((chatObject) => {
        this.chatStore.fetchMessages(chatObject)
      })
    },

    printDate(chatPublishedAt, chatIndex) {
      if (chatIndex !== 0) {
        const cd = new Date(chatPublishedAt)
        const pd = new Date(this.chatMessages[chatIndex - 1].published_at)
        return cd.getDate() !== pd.getDate()
      } else {
        return true
      }
    },

    chatWidget(chat) {
      if (chat.deleted_at) {
        return 'ChatMessage'
      }

      // old chat fallback maybe delete this if block along with this comment after
      const isImageMessage = !!chat.image_url
      const isAttachment = !!chat.attachment_url
      if (isAttachment || isImageMessage) {
        return isAttachment || isImageMessage ? 'ImageWidget' : 'ChatMessage'
      }

      if (chat.message_rich.error_type === 'file_upload') {
        chat.message_rich.message = this.$t('file_upload.file_upload_failed')
        chat.message_rich.filename = chat.message
        return 'UploadError'
      }

      // return the components under 'Chats' folder according to chat type
      switch (chat.message_rich.type) {
        case 'plain':
          return 'PlainTextWidget'
        case 'upload_error':
          return 'UploadError'
        case 'action':
        case 'operator':
        case 'time':
        case 'form_action':
        case 'booking_widget':
          return 'ActionWidget'
        case 'hotel_list':
          return 'ImageListWidget'
        case 'date_time':
          return 'DateTimeWidget'
        // TODO: check and remove this AM type
        case 'email':
          this.$rollbar.info(`Email widget rendered ${this.chatStore.chatId}`)
          return 'EmailWidget'
        case 'template':
        case 'collect_user_input_form':
          return 'TemplateWidget'
        case 'auto_message':
          if (chat.message_rich.auto_message_info) {
            switch (chat.message_rich.auto_message_info.type) {
              case 'question':
              case 'menu':
              case 'auth':
                return 'ActionWidget'
            }
          }
          return 'AutoMessageWidget'
        case 'advertisment':
        case 'auto_message_image':
          return 'AdvertismentWidget'
        case 'brr':
          this.$rollbar.info(`BRR widget rendered ${this.chatStore.chatId}`)
          return 'BrrWidget'
        case 'credit_card':
          return 'CreditCardWidget'
        case 'date':
          return 'DateWidget'
        case 'options':
          return 'SelectorWidget'
        case 'login':
          return 'LoginWidget'
        case 'language':
        case 'language_selector':
          return 'LanguageWidget'
        case 'media':
          return 'MediaWidget'
        case 'multi_select':
          return 'MultiSelectWidget'
        case 'slider':
        case 'collect_user_input_slider':
          return 'SliderWidget'
        case 'quick_reply':
          return 'QuickReplyWidget'
        case 'user_evaluation':
          return 'UserEvaluationWidget'
        default:
          return 'ChatMessage'
      }
    },
    widgetProps: function (chat, index) {
      const baseProps = {
        chat: chat,
        prevChat: this.transformedChatMessages[index - 1],
        operatorName: this.operatorName
      }

      if (!chat.message_rich) {
        return baseProps
      }

      switch (chat.message_rich.type) {
        case 'auto_message_image':
        case 'email':
        case 'media':
        case 'action':
        case 'auto_message':
        case 'date':
        case 'date_time':
        case 'options':
        case 'multi_select':
        case 'operator':
        case 'quick_reply':
        case 'user_evaluation':
        case 'time':
        case 'hotel_list':
        case 'booking_widget':
        case 'language_selector':
        case 'slider':
        case 'collect_user_input_slider':
        case 'brr':
          if (
            chat.message_rich.auto_message_info &&
            chat.message_rich.auto_message_info.type === 'auth'
          ) {
            return {
              ...baseProps,
              openUserPanel: this.openUserPanel
            }
          }

          return baseProps
        case 'form_action':
          return {
            ...baseProps,
            initForm: this.chatStore.initForm
          }
        case 'credit_card':
          return {
            ...baseProps,
            choosePaymentMethod: this.choosePaymentMethod,
            newPaymentMethod: this.newPaymentMethod
          }
        case 'login':
          return {
            ...baseProps,
            openUserPanel: this.openUserPanel
          }
        default:
          return baseProps
      }
    },

    isBrowserObsolete() {
      const ua = window.navigator.userAgent // Check the userAgent property of the window.navigator object
      const isIE10OrBelow = ua.indexOf('MSIE ') > 0 // IE 10 or older
      const isIE11 = ua.indexOf('Trident/') > 0 // IE 11

      return isIE10OrBelow || isIE11
    }
  }
}
</script>
